import { ListResult } from '@lemoncloud/codes-backend-api/dist/cores/types';
import { CatalogView, SettingsView } from '@lemoncloud/codes-goods-api';
import { Params } from '@lemoncloud/lemon-web-core';

import { webCore } from '@codes/web-core';

import { CreateCatalogBody, UpdateCatalogBody } from '../types';

const BACKEND_ENDPOINT = import.meta.env.VITE_BACKEND_ENDPOINT.toLowerCase();
const IMAGE_API_ENDPOINT = import.meta.env.VITE_IMAGE_API_ENDPOINT.toLowerCase();

export interface UploadedImage {
    contentLength: number;
    contentType: string;
    createdAt: number;
    hash: string;
    height?: number;
    id: string;
    location: string;
    name: string;
    ns: string;
    origin: string;
    type: string;
    url: string;
    width?: number;
}

export const fetchCatalogs = async (params: Params): Promise<ListResult<CatalogView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/catalogs/0/list`,
        })
        .setParams({ ...params })
        .execute<ListResult<CatalogView>>();

    return { ...data };
};

export const fetchCatalogById = async (catalogId?: string): Promise<CatalogView> => {
    if (!catalogId) {
        throw new Error('fetchCatalogById: @catalogId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/catalogs/${catalogId}/detail`,
        })
        .execute<CatalogView>();

    return { ...data };
};

export const fetchCatalogSettingsById = async (catalogId?: string): Promise<SettingsView> => {
    if (!catalogId) {
        throw new Error('fetchCatalogSettingsById: @catalogId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/catalogs/${catalogId}/settings`,
        })
        .setParams({
            config: 1,
            environ: 1,
        })
        .execute<SettingsView>();

    return data;
};

export const createCatalog = async (createCatalog: CreateCatalogBody): Promise<CatalogView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BACKEND_ENDPOINT}/catalogs/0`,
        })
        .setBody({ ...createCatalog.body })
        .execute<CatalogView>();

    return data;
};

export const updateCatalog = async (updateCatalogBody: UpdateCatalogBody): Promise<CatalogView> => {
    const { catalogId, body } = updateCatalogBody;
    if (!catalogId) {
        throw new Error('updateCatalog: @catalogId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/catalogs/${catalogId}`,
        })
        .setBody({ ...body })
        .execute<CatalogView>();

    return data;
};

export const deleteCatalog = async (catalogId: string): Promise<CatalogView> => {
    if (!catalogId) {
        throw new Error('deleteCatalog: @catalogId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${BACKEND_ENDPOINT}/catalogs/${catalogId}`,
        })
        .setBody({})
        .execute<CatalogView>();

    return data;
};

export const uploadImage = async (
    image: File,
    onUploadProgress?: (event: unknown) => void
): Promise<UploadedImage | null> => {
    if (!image) {
        throw new Error('@image is required');
    }
    const extraConfig = onUploadProgress ? { onUploadProgress } : {};
    const form = new FormData();
    form.append('file', image);

    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${IMAGE_API_ENDPOINT}/upload`,
        })
        .setParams({})
        .setBody(form)
        .addHeaders({
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'multipart/form-data',
        })
        .addAxiosRequestConfig(extraConfig)
        .execute();

    try {
        return data.list[0];
    } catch (e) {
        return null;
    }
};
